import presizeBackendRest from '../../services/presize-backend-rest';
import { isDbMigrated } from '../../lib/rest-feature-flag';
import { RestApiError } from '../../lib/rest-api';
import { mapRestOrderToStoreOrder } from '../../lib/adapters/order-adapter';
import { mapRestGroupsToStoreGroups } from '../../lib/adapters/article-group-adapter';
import { Article, SizingData } from '../../models/sizing-data';
import { OrderData } from '../../models/sizing-group';
import { SizingGroup } from '../../models/sizing-group';
import { ArticleRequestInput } from '../../models/rest/article-groups';
import { GetOrderResponse, CreateOrderResponse } from '../../models/rest/order';
import { sendEvent } from '../../services/event-tracking';
import { AdditionalData } from '../../services/event-tracking';

/**
 * Result type for order and groups creation
 */
export interface OrderAndGroupsResult {
  order: OrderData; // Adapted order in store format (camelCase)
  groups?: SizingGroup[]; // REST groups only (isDbMigrated=true); undefined → store calls groupArticles()
  isNewOrderCreated: boolean; // Flag for first-time vs returning user
}

/**
 * HYBRID PHASE 1: Order operations via REST, article groups via GraphQL or REST
 *
 * Migration to Phase 2 (full REST):
 * 1. Wait for backend DB migrations to complete
 * 2. Set flag: localStorage.setItem('PRESIZE_DB_MIGRATIONS_COMPLETE', 'true')
 * 3. Article groups will automatically switch to REST
 *
 * @param referenceId - Order code from BHOS
 * @param articles - Articles from BHOS
 * @param sizingData - Sizing context (season, brand, etc.)
 * @returns Promise with order, groups, and isNewOrderCreated flag
 */
export async function createOrderAndGroups(
  referenceId: string,
  articles: Article[],
  sizingData: SizingData | Pick<SizingData, 'seasonCode' | 'brandCode'>
): Promise<OrderAndGroupsResult> {
  // STEP 1: Always use REST for order operations (migration-ready)
  let order: GetOrderResponse | CreateOrderResponse;
  let isNewOrder = false;

  try {
    // Try to fetch existing order
    order = await presizeBackendRest.getOrder(referenceId);
  } catch (err) {
    if (err instanceof RestApiError && err.status === 404) {
      // Expected: Order doesn't exist yet (first-time user)
      // Create order with default settings
      order = await presizeBackendRest.createOrder({
        order_code: referenceId,
        season_code: sizingData.seasonCode,
        brand_code: sizingData.brandCode,
        cg_grouping_level: 3, // Default to CG3
        enable_box: false, // Default disabled
        status: 'DRAFTED',
      });

      isNewOrder = true;
      // Emitted here rather than from the store: this is the only point that knows the order was
      // just created, and it cannot be lost to a re-render, abort or teardown further down (PT-58792).
      sendEvent(undefined, 'orderCreated', undefined, orderCreatedEventData(referenceId, sizingData));
    } else {
      // Non-404 errors should propagate (400, 500, etc.)
      throw new Error(`Failed to fetch order: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  // Adapt order to store format (needed for both paths)
  const adaptedOrder = mapRestOrderToStoreOrder(order);

  // STEP 2: REST article groups only when migrations are complete.
  // When not migrated, groups is omitted → SizingStore falls back to groupArticles()
  // which calls GraphQL updateOrder + loadSizing() as before.
  if (isDbMigrated()) {
    const groupsResponse = await presizeBackendRest.createArticleGroups(referenceId, {
      articles: articles.map(mapArticleToRestFormat),
    });
    const adaptedGroups = mapRestGroupsToStoreGroups(groupsResponse.groups, adaptedOrder.groupingLevel, articles);
    return { order: adaptedOrder, groups: adaptedGroups, isNewOrderCreated: isNewOrder };
  }

  return { order: adaptedOrder, isNewOrderCreated: isNewOrder };
}

/**
 * The orderCreated payload only reads orderReferenceId; the remaining fields satisfy the shared type.
 */
function orderCreatedEventData(
  referenceId: string,
  sizingData: SizingData | Pick<SizingData, 'seasonCode' | 'brandCode'>
): AdditionalData {
  const data = sizingData as Partial<SizingData>;
  return {
    supplierCode: data.supplierCode ?? '',
    subUnitCode: data.subunitCode ?? '',
    buyerEmail: undefined,
    brandCode: sizingData.brandCode,
    brandName: data.brandName,
    orderReferenceId: referenceId,
    season: sizingData.seasonCode,
    cgGroupingLevel: undefined,
    isImportSupplierStockUsed: undefined,
  };
}

/**
 * Maps article from BHOS format to REST API format
 * BHOS uses camelCase, REST API expects snake_case
 */
export function mapArticleToRestFormat(article: Article): ArticleRequestInput {
  return {
    reference_id: article.referenceId,
    buying_article_config_id: article.buyingArticleConfigId || '',
    commodity_group_code: article.commodityGroup.code,
    size_structure: article.sizeStructure || null,
    sizing_note: article.sizingNote || null,
    quantity: article.quantity,
    size_chart_editable: article.sizeChartEditable,
    size_chart_code: article.sizeChartCode || null,
    length_chart_code: article.lengthChartCode || null,
    size_length_separator: null, // Not in Article model
    silo: article.silo || null,
    size_list: article.sizing ? Object.keys(article.sizing) : null,
    is_reviewed: article.reviewed || false,
    reference_brand_code: null, // Not in Article model yet
    box_restriction_id: article.boxing?.boxRestrictionId ? parseInt(article.boxing.boxRestrictionId, 10) : null,
    reused_decision_id: null, // Not in Article model yet
  };
}
